import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { Loader2, Maximize2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Perspective, ProcessStep, ProcessType, perspectiveTitle } from './processModel';
import PerspectiveValueList from './perspectiveValues';
import { perspectiveAttributes } from './perspectiveAttributes';
import { PerspectiveScope, PerspectiveValues } from './sitePerspectiveApi';
import { openCount, usePhaseChecklists } from './usePhaseChecklists';
import { sampleContentFor } from './sampleContent';

/**
 * The perspectives of the selected process step, one tile each. The tiles carry
 * no content yet — they are the places the step's content will be attached to,
 * which is why picking one is already tracked in the URL. The expand icon opens
 * the perspective's drill-down panel.
 */

interface Properties {
    type: ProcessType;
    step: ProcessStep;
    color: string;
    selectedPerspective: string | null;
    onSelectPerspective: (key: string) => void;
    onExpandPerspective: (perspective: Perspective) => void;
    /** Stored values of the site perspectives, where the step is backed by them. */
    perspectiveValues?: PerspectiveValues;
    /** True while the phase these perspectives belong to has not been activated. */
    perspectivesLocked?: boolean;
    /** True while it is not yet known whether the phase is activated — the tiles wait rather than guess. */
    perspectivesLoading?: boolean;
    /** The phase on screen — the checklist point of the project status is read from it. */
    perspectiveScope?: PerspectiveScope | null;
}

export default function PerspectiveTiles({
    type,
    step,
    color,
    selectedPerspective,
    onSelectPerspective,
    onExpandPerspective,
    perspectiveValues,
    perspectivesLocked,
    perspectivesLoading,
    perspectiveScope,
}: Properties) {
    const { t, i18n } = useTranslation();
    // One read for the whole step: only the project-status tile shows a checklist figure, but the
    // drill-down behind it shows the same rows, and both should agree.
    const { rows: checklistRows, loading, error } = usePhaseChecklists(perspectivesLocked ? null : perspectiveScope ?? null);
    const tint = tinycolor(color).setAlpha(0.14).toRgbString();
    const wash = tinycolor(color).setAlpha(0.07).toRgbString();

    return (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
            {step.perspectives.map((perspective, index) => {
                const Icon = perspective.icon;
                const selected = perspective.key === selectedPerspective;
                const title = perspectiveTitle(perspective, t, i18n.language ?? 'de');
                const content = sampleContentFor(type.key, step.key, perspective.key);
                // Was der Typ der Perspektive fragt, plus was diese Phase dazulegt — dieselbe
                // Perspektive verlangt in der Ansprache anderes als in der Ausführung.
                const attributes = perspective.perspectiveType
                    ? perspectiveAttributes(type.key, step.key, perspective.perspectiveType)
                    : null;

                return (
                    // A div, not a button: the expand control is a button of its
                    // own and must not be nested inside another one.
                    <div
                        key={perspective.key}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selected}
                        className={cn(
                            'relative flex min-h-[210px] cursor-pointer flex-col overflow-hidden rounded-xl border bg-card text-left shadow-sm transition',
                            "before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-[color:var(--step-color)] before:opacity-90 before:content-['']",
                            'hover:-translate-y-0.5 hover:shadow-lg',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--step-color)] focus-visible:ring-offset-2',
                            selected &&
                                'border-[color:var(--step-color)] shadow-lg ring-1 ring-[color:var(--step-color)]',
                        )}
                        style={
                            { '--step-color': color, '--step-tint': tint, '--step-wash': wash } as React.CSSProperties
                        }
                        onClick={() => onSelectPerspective(perspective.key)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onSelectPerspective(perspective.key);
                            }
                        }}
                    >
                        <div className="flex items-center gap-2 border-b px-3 py-2">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:var(--step-tint)] text-[color:var(--step-color)]">
                                <Icon size={16} strokeWidth={1.9} />
                            </span>
                            <span className="min-w-0 flex-1 text-sm font-bold leading-tight">{title}</span>
                            <span className="shrink-0 text-xs font-bold text-muted-foreground">
                                {String(index + 1).padStart(2, '0')}
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="shrink-0 text-muted-foreground"
                                title={t('portfolioDashboard.drillDown.open', { perspective: title })}
                                aria-label={t('portfolioDashboard.drillDown.open', { perspective: title })}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onSelectPerspective(perspective.key);
                                    onExpandPerspective(perspective);
                                }}
                            >
                                <Maximize2 size={14} strokeWidth={2} />
                            </Button>
                        </div>
                        {/* Canvas for the perspective's figures — empty until a
                            perspective has content of its own. */}
                        <div className="min-h-0 flex-1 bg-[radial-gradient(120%_110%_at_100%_100%,var(--step-wash)_0%,transparent_62%)] px-3 pb-3 pt-2.5">
                            {attributes && perspectivesLoading ? (
                                // Noch keine Antwort: weder „nicht aktiviert" noch leere Werte behaupten.
                                <Loader2
                                    size={14}
                                    strokeWidth={2}
                                    className="animate-spin text-muted-foreground"
                                    aria-label={t('global.loading')}
                                />
                            ) : attributes && perspectivesLocked ? (
                                // Empty on its own would read as "no data"; this is "not yet asked
                                // for", which is a different thing and the reader's next action.
                                <span className="text-xs text-destructive">
                                    {t('portfolioDashboard.activation.locked')}
                                </span>
                            ) : attributes ? (
                                // Alle Werte, in der Kachel scrollbar — die Kachel wächst nicht, und
                                // was unter der fünften Zeile steht, ist trotzdem zu sehen.
                                <div className="max-h-[240px] overflow-y-auto pr-1">
                                    <PerspectiveValueList
                                        attributes={attributes}
                                        values={perspectiveValues?.[perspective.perspectiveType!]}
                                        openChecklistTasks={loading || error ? undefined : openCount(checklistRows)}
                                    />
                                </div>
                            ) : content ? (
                                <>
                                    <div className="mb-2 text-xs leading-snug text-muted-foreground">
                                        {content.headline}
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        {content.highlights.map((metric) => (
                                            <div
                                                key={metric.label}
                                                className="flex items-baseline justify-between gap-2 border-b border-dashed py-0.5"
                                            >
                                                <span className="truncate text-xs uppercase tracking-wide text-muted-foreground">
                                                    {metric.label}
                                                </span>
                                                <span className="whitespace-nowrap text-sm font-bold">
                                                    {metric.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
